import * as React from 'react'
import {
  Box,
  Divider,
  InputLabel,
  InputAdornment,
  Icon,
  styled,
} from '@design/pt-ui'
import * as LodashFP from 'lodash/fp'
import * as Lodash from 'lodash'

import Select from '../../components/Select'
import DebouncedTextField from '../../components/DebouncedTextField'

import { Project } from '../../interfaces/entities/project'

import { CreativesFilter } from './hooks'
import { nicer } from '../../utils/string'
import { IStringAnyMap } from '../../types/map'

import { translateWithUserLocale } from '../../utils/i18n'
import { getBlueprintAtPath } from '../../lib/dynamic-form/helpers'
import { Platform } from '../../interfaces/entities/platform'

const FilterColumn = styled('div', {
  shouldForwardProp: (prop) => prop !== 'wide',
})<{ wide?: boolean }>(({ theme, wide }) => ({
  display: 'flex',
  flexFlow: 'column',
  marginRight: theme.spacing(3),
  textAlign: 'left',
  flex: wide ? 5 : 4,
}))

interface ICreativeFilterProps {
  project: Project
  platforms: Platform[]
  filter: CreativesFilter
}

function CreativeFilter(props: ICreativeFilterProps) {
  const {
    state,
    onQueryChange,
    onFormatsChange,
    onStatesChange,
    onPlacementsChange,
    onPlatformsChange,
  } = props.filter

  const formatsets = props.project.formatsets

  const creativeFormats = LodashFP.uniqBy(
    'value',
    LodashFP.flatten(
      formatsets.map((fs) => {
        return fs.creativeFormats.map((cf) => ({
          value: cf.name,
          label: cf.label,
        }))
      }),
    ),
  )

  const creativeStates = props.project.creativeStates.map((state) => {
    return {
      value: state.name,
      label: state.label,
    }
  })

  const creativePlatforms = props.platforms.map((platform) => {
    return {
      value: platform.value,
      label: platform.label,
    }
  })

  const formatsetsWithLabeledPlacements = formatsets
    .filter((formatset) => !formatset.invalid)
    .map((formatset) => {
      return {
        ...formatset,
        data: {
          ...formatset.data,
          placement: {
            ...Lodash.mapKeys(
              Lodash.mapValues(
                formatset.data.placement,
                (placements: any, group: string) => {
                  const path = `placement.${group}`
                  const fieldBlueprint = formatset.blueprint
                    ? getBlueprintAtPath(formatset.blueprint, path)
                    : null

                  const placementsArray =
                    typeof placements === 'string' ? [placements] : placements

                  return placementsArray.map((value: any) => {
                    let label = nicer(value)

                    if (
                      fieldBlueprint !== null &&
                      fieldBlueprint.type === 'select'
                    ) {
                      const option = fieldBlueprint.meta.options.find(
                        (option) => {
                          return option.value === value
                        },
                      )

                      if (option) {
                        label = option.label
                      }
                    }

                    return { value, label }
                  })
                },
              ),
              (_, key) => {
                const fieldBlueprint = formatset.blueprint
                  ? getBlueprintAtPath(formatset.blueprint, 'placement')
                  : null

                let label = nicer(key)

                if (fieldBlueprint && fieldBlueprint.type === 'fieldset') {
                  for (
                    let index = 0;
                    index < fieldBlueprint.fields.length;
                    index++
                  ) {
                    const field = fieldBlueprint.fields[index]

                    if (field.name === key) {
                      label = field.label
                    }
                  }
                }

                return `${key}__==__${label}`
              },
            ),
          },
        },
      }
    })

  const mergedPlacements: IStringAnyMap =
    formatsetsWithLabeledPlacements.reduce((acc, formatset) => {
      return Lodash.mergeWith(
        acc,
        formatset.data.placement,
        function customizer(objValue, srcValue, key, object, source) {
          if (Lodash.isArray(objValue)) {
            return Lodash.uniqWith(objValue.concat(srcValue), Lodash.isEqual)
          }
        },
      )
    }, {})

  const groupedPlacements: any[] = []
  Object.keys(mergedPlacements).forEach((key) => {
    const [group, label] = key.split('__==__')

    groupedPlacements.push({
      label,
      options: mergedPlacements[key].map(
        (obj: { value: string; label: string }) => {
          const { value, label } = obj

          return { label, value: `${group}__$__${value}`, group }
        },
      ),
    })
  })

  return (
    <Box sx={{ padding: 0.5, textAlign: 'left', marginBottom: '25px' }}>
      <Box
        sx={{
          display: 'flex',
          flexFlow: 'row',
          padding: 3,
          alignItems: 'flex-end',
        }}>
        <FilterColumn wide={true} id="cp-filter-platform">
          <InputLabel>Creatives Platform</InputLabel>
          <Select
            isMulti={true}
            options={creativePlatforms}
            value={state.platforms}
            onChange={onPlatformsChange}
            placeholder={translateWithUserLocale('projects.select_platform')}
          />
        </FilterColumn>
        <FilterColumn id="cp-filter-status">
          <InputLabel>Creatives Status</InputLabel>
          <Select
            isMulti={true}
            options={creativeStates}
            value={state.states}
            onChange={onStatesChange}
            placeholder={translateWithUserLocale('projects.select_status')}
          />
        </FilterColumn>
        <FilterColumn id="cp-filter-format">
          <InputLabel>Creatives Format</InputLabel>
          <Select
            isMulti={true}
            value={state.formats}
            onChange={onFormatsChange}
            options={creativeFormats}
            placeholder={translateWithUserLocale('projects.select_format')}
          />
        </FilterColumn>
        <FilterColumn id="cp-filter-placement">
          <InputLabel>
            {translateWithUserLocale('projects.filter_placement')}
          </InputLabel>
          <Select
            isMulti={true}
            options={groupedPlacements}
            value={state.placements}
            onChange={onPlacementsChange}
            placeholder={translateWithUserLocale('projects.select_placement')}
          />
        </FilterColumn>
        <Box sx={{ flex: 4, marginBottom: 0 }}>
          <DebouncedTextField
            value={state.query}
            onChange={onQueryChange}
            id="cp-filter-search"
            text={{
              type: 'search',
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon>search</Icon>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Box>
      <Divider
        component="h3"
        sx={{ marginTop: '25px', marginBottom: '25px' }}
      />
    </Box>
  )
}

export default CreativeFilter
